import React, { useEffect, useState, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { motion } from "framer-motion";
import { Modal, Spin, Empty } from "antd";
import { LoadingOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../store";
import defaultAvatar from "../../assets/default_avatar.png";
import { ReactComponent as PictureIcon } from "../../assets/picture.svg";
import {
  createPostThunk,
  getPostStatsThunk,
  getNotificationsThunk,
  getPostsThunk,
  deletePostThunk,
} from "../../store/actions/social.action";
import { message } from "antd";
import { useOptimisticUpdate } from "../../core/utils/useOptimisticUpdate";
import { PostCard, PostType } from "../../components/Social/PostCard";
import { useSearch } from "../../context/SearchContext";
import Avatar from "boring-avatars";
import { getUserInfoThunk } from "../../store/actions/auth.action";
import { useSocialContext } from "../../context/socialContext";
import { useLocation, useParams } from "react-router-dom";
import AuthGuard from "../../components/AuthGuard";

interface CommentType {
  _id: string;
  content: string;
  createdAt: string;
  user: {
    avatarUrl: string;
    firstName: string;
    lastName: string;
    email: string;
    paymentMode: number;
  };
}

interface StatsType {
  stats: {
    post: {
      [countryCode: string]: number; // e.g., "VN": 6, "US": 3, etc.
    };
  };
  posts: number;
  reactions: number;
}

// Add GlobalModalStyle
const GlobalModalStyle = createGlobalStyle`
  .social-modal {
    .ant-modal {
      padding: 0;
    }
    .ant-modal-content {
      background: #282828;
      border-radius: 24px;
      overflow: hidden;
      padding: 0;
    }
    .ant-modal-body {
      padding: 0 !important;
      background: #282828;
      border-radius: 24px;
    }
    .ant-modal-close {
      color: #bdbdbd;
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
    .ant-modal-mask {
      backdrop-filter: blur(4px);
      background: rgba(0, 0, 0, 0.45);
    }
  }
`;

// Add CreatePostModal component
const CreatePostModal = ({
  isOpen,
  onClose,
  onPostCreated,
  updatePosts,
  removeOptimisticPost,
  countryCode,
  setCurrentPage,
}: {
  isOpen: boolean;
  onClose: () => void;
  onPostCreated: (post: PostType) => void;
  updatePosts: (posts: PostType[]) => void;
  removeOptimisticPost: (postId: string) => void;
  countryCode: string;
  setCurrentPage: (page: number) => void;
}) => {
  const [newPost, setNewPost] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const { me, userExtendData } = useAppSelector((state) => state.authReducer);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length) {
      setSelectedFiles((prev) => [...prev, ...files]);
      setPreviewUrls((prev) => {
        const newUrls = files.map((file) => URL.createObjectURL(file));
        return [...prev, ...newUrls];
      });
    }
    e.target.value = "";
  };

  const handleSubmit = async () => {
    if (newPost.trim() || selectedFiles.length > 0) {
      // Create optimistic post
      const optimisticPost: PostType = {
        _id: "temp-" + Date.now(),
        author: me?.email || "",
        avatar: userExtendData?.avatarUrl || defaultAvatar,
        verified: false,
        title: "",
        content: newPost,
        videos: [],
        images: [],
        likes: 0,
        commentCount: 0,
        createdAt: new Date().toISOString(),
        user: {
          _id: me?._id || me?.id || "",
          avatarUrl: userExtendData?.avatarUrl || "",
          firstName: me?.firstName || "",
          lastName: me?.lastName || "",
          email: me?.email || "",
          paymentMode: me?.paymentMode || 0,
        },
        userReaction: "",
        reactionSummary: [],
      };

      // Add optimistic post
      onPostCreated(optimisticPost);
      onClose();
      setNewPost("");
      setSelectedFiles([]);
      setPreviewUrls([]);

      try {
        const result = await dispatch(
          createPostThunk({
            content: newPost,
            media: selectedFiles,
            countryCode: countryCode,
          })
        ).unwrap();

        if (result) {
          const posts = await dispatch(
            getPostsThunk({ page: 1, limit: 10, countryCode: countryCode })
          ).unwrap();

          if (posts) {
            updatePosts(posts.data as PostType[]);
            setCurrentPage(1);
          }
        }
      } catch (error) {
        message.error("Failed to create post");
        removeOptimisticPost(optimisticPost._id);
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      wrapClassName="social-modal"
      style={{ padding: "20px 0", overflow: "hidden" }}
      closeIcon={null}
    >
      <ModalContent>
        <ModalHeader>
          <h2>Create Post</h2>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          <ComposerRowModal>
            {userExtendData?.avatarUrl ? (
              <img
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                src={userExtendData?.avatarUrl || defaultAvatar}
              />
            ) : (
              <Avatar
                name={me?.firstName + " " + me?.lastName}
                variant="beam"
                size={52}
              />
            )}
            <ComposerTextAreaInput
              rows={3}
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="Share your thoughts..."
            />
          </ComposerRowModal>
          {previewUrls.length > 0 && (
            <PreviewContainer>
              {previewUrls.map((url, idx) => (
                <PreviewItem key={url}>
                  <RemoveButton
                    onClick={() => {
                      setSelectedFiles((files) =>
                        files.filter((_, i) => i !== idx)
                      );
                      setPreviewUrls((urls) =>
                        urls.filter((_, i) => i !== idx)
                      );
                    }}
                  >
                    ✕
                  </RemoveButton>
                  {selectedFiles[idx]?.type.startsWith("image/") ? (
                    <img src={url} alt="preview" />
                  ) : (
                    <video src={url} controls />
                  )}
                </PreviewItem>
              ))}
            </PreviewContainer>
          )}
          <ComposerActions>
            <ComposerAction type="button" onClick={handleFileButtonClick}>
              <PictureIcon style={{ width: 24, height: 24 }} />{" "}
              <span>Photo</span>
            </ComposerAction>
            <ComposerAction type="button" onClick={handleFileButtonClick}>
              <VideoCameraOutlined style={{ color: "#60a5fa" }} />{" "}
              <span>Video</span>
            </ComposerAction>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </ComposerActions>
        </ModalBody>
        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <PostButton
            onClick={handleSubmit}
            disabled={!newPost.trim() && selectedFiles.length === 0}
          >
            Post
          </PostButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const OptimisticPostWrapper = styled.div<{ isOptimistic?: boolean }>`
  opacity: ${(props) => (props.isOptimistic ? 0.6 : 1)};
  transition: opacity 0.3s ease-in-out;
`;

const QA = () => {
  const location = useLocation();
  const { id: userId } = useParams();
  const { community } = useSocialContext();
  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
  const { me, userExtendData } = useAppSelector((state) => state.authReducer);
  const [stats, setStats] = useState<StatsType>();
  const { posts } = useAppSelector((state) => state.socialReducer);
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [
    optimisticPosts,
    addOptimisticPost,
    removeOptimisticPost,
    updatePosts,
  ] = useOptimisticUpdate<PostType>(posts.data || []);
  const { hashtags, content } = useSearch();
  const { notifications } = useAppSelector((state) => state.socialReducer);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [isLoadingMoreNotifications, setIsLoadingMoreNotifications] =
    useState(false);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const userPostsRegex = new RegExp("^/user/[^/]+$");

  const fetchUserInfo = async () => {
    try {
      await dispatch(getUserInfoThunk());
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPosts = async (page: number, isLoadMore: boolean = false) => {
    if (isLoadingMore) return;
    try {
      setIsLoadingMore(true);
      const result = await dispatch(
        getPostsThunk({
          page,
          limit: 10,
          hashtag: hashtags.length > 0 ? hashtags : undefined,
          content: content || undefined,
          countryCode: !!userId
            ? undefined
            : community === "GLOBAL"
            ? "GLOBAL"
            : community,
          userId: !!userId ? userId : undefined,
        })
      ).unwrap();

      if (result) {
        const newPosts = result.data as PostType[];
        if (isLoadMore) {
          updatePosts([...optimisticPosts, ...newPosts]);
        } else {
          updatePosts(newPosts);
        }
        setHasMore(newPosts.length === 10);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const fetchPostStats = async () => {
    try {
      setIsLoadingMore(true);
      const result = await dispatch(getPostStatsThunk()).unwrap();

      if (result) {
        const stats = result as StatsType;
        setStats(stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const fetchNotifications = async (
    page: number = 1,
    append: boolean = false
  ) => {
    try {
      setIsLoadingMoreNotifications(true);
      const result = await dispatch(
        getNotificationsThunk({ page, limit: 10 })
      ).unwrap();

      if (result) {
        if (append) {
          // Append new notifications to existing ones
          dispatch({
            type: "social/getNotifications/fulfilled",
            payload: {
              ...result,
              data: [...notifications.data, ...result.data],
            },
          });
        }

        // Check if there are more notifications to load
        setHasMoreNotifications(result.data.length === 10);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoadingMoreNotifications(false);
    }
  };

  const handleOptimisticDelete = async (postId: string) => {
    // Remove post optimistically
    const postToDelete = optimisticPosts.find((post) => post._id === postId);
    if (!postToDelete) return;

    // Store the current posts in case we need to restore
    const currentPosts = [...optimisticPosts];

    // Remove the post from the UI immediately
    updatePosts(optimisticPosts.filter((post) => post._id !== postId));

    try {
      // Attempt to delete the post
      await dispatch(deletePostThunk({ postId })).unwrap();
      message.success("Post deleted successfully");
    } catch (error) {
      // If deletion fails, restore the post
      message.error("Failed to delete post");
      updatePosts(currentPosts);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchPosts(1);
  }, [hashtags, content, community, userId]);

  useEffect(() => {
    setNotificationsPage(1);
    fetchNotifications(1);
    fetchUserInfo();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const clientHeight = document.documentElement.clientHeight;

      // Load more when user has scrolled to 80% of the content
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        !isLoadingMore &&
        hasMore
      ) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        fetchPosts(nextPage, true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentPage, isLoadingMore, hasMore, optimisticPosts]);

  useEffect(() => {
    const handleNotificationsScroll = () => {
      if (!notificationsRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        notificationsRef.current;

      // Load more when user has scrolled to 80% of the content
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        !isLoadingMoreNotifications &&
        hasMoreNotifications
      ) {
        const nextPage = notificationsPage + 1;
        setNotificationsPage(nextPage);
        fetchNotifications(nextPage, true);
      }
    };

    const notificationsContainer = notificationsRef.current;
    if (notificationsContainer) {
      notificationsContainer.addEventListener(
        "scroll",
        handleNotificationsScroll
      );
    }

    return () => {
      if (notificationsContainer) {
        notificationsContainer.removeEventListener(
          "scroll",
          handleNotificationsScroll
        );
      }
    };
  }, [
    notificationsPage,
    isLoadingMoreNotifications,
    hasMoreNotifications,
    notifications.data,
  ]);

  return (
    <MotionWrapper
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <GlobalModalStyle />
      <MainContent>
        {!(community === "KARDO" && !me?.email?.endsWith("@kardo.ai")) &&
          (userId === me?._id || !userId) && (
            <ComposerCard>
              <ComposerRow>
                {userExtendData?.avatarUrl ? (
                  <img
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                    src={userExtendData?.avatarUrl || defaultAvatar}
                  />
                ) : (
                  <Avatar
                    name={me?.firstName + " " + me?.lastName}
                    variant="beam"
                    size={52}
                  />
                )}
                <AuthGuard>
                  <ComposerInput
                    type="text"
                    placeholder="Share your thoughts..."
                    onClick={() => {
                      if (!me) return;
                      setIsCreatePostModalOpen(true);
                    }}
                    readOnly={!me}
                  />
                </AuthGuard>
              </ComposerRow>
              <ComposerActions>
                <AuthGuard>
                  <ComposerAction
                    type="button"
                    onClick={() => {
                      if (!me) return;
                      setIsCreatePostModalOpen(true);
                    }}
                  >
                    <PictureIcon style={{ width: 24, height: 24 }} />{" "}
                    <span id="photo-span">Photo</span>
                  </ComposerAction>
                </AuthGuard>
                <AuthGuard>
                  <ComposerAction
                    type="button"
                    onClick={() => {
                      if (!me) return;
                      setIsCreatePostModalOpen(true);
                    }}
                  >
                    <VideoCameraOutlined style={{ color: "#60a5fa" }} />{" "}
                    <span id="video-span">Video</span>
                  </ComposerAction>
                </AuthGuard>
              </ComposerActions>
            </ComposerCard>
          )}

        {/* Feed */}
        {/* {isLoadingMore ? (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        ) : ( */}
        {!!userPostsRegex.test(location.pathname) &&
          !!optimisticPosts.length && (
            <h2 style={{ margin: 0 }}>
              {optimisticPosts?.[0]?.user?.firstName}'s Post
            </h2>
          )}

        {optimisticPosts.map((post) => (
          <OptimisticPostWrapper
            key={post._id}
            isOptimistic={post._id.startsWith("temp-")}
          >
            <PostCard post={post} onDelete={handleOptimisticDelete} />
          </OptimisticPostWrapper>
        ))}

        {optimisticPosts.length === 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <StyledEmpty
              description={
                <span style={{ color: "#686868" }}>No posts found</span>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </MainContent>
      <CreatePostModal
        isOpen={isCreatePostModalOpen}
        onClose={() => setIsCreatePostModalOpen(false)}
        onPostCreated={addOptimisticPost}
        updatePosts={(posts: PostType[]) => {
          updatePosts(posts);
          fetchPostStats();
        }}
        removeOptimisticPost={removeOptimisticPost}
        countryCode={community}
        setCurrentPage={setCurrentPage}
      />
    </MotionWrapper>
  );
};

// Styled components
const MotionWrapper = motion(styled.div`
  height: 100%;
  transition: width 0.3s ease-in-out;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-direction: column;
  box-sizing: border-box;
  width: calc(50% - 3rem);

  @media (max-width: 1400px) {
    width: calc(75% - 1.5rem);
  }

  @media (max-width: 1200px) {
    width: 100%;
  }
`);

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const ComposerCard = styled.div`
  background: #282828;
  border-radius: 24px;
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  box-sizing: border-box;
`;

const ComposerRowModal = styled.div`
  display: flex;
  gap: 1rem;
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ComposerTextAreaInput = styled.textarea`
  flex: 1;
  background: #222222;
  color: #bdbdbd;
  border: none;
  outline: none;
  border-radius: 14px;
  padding: 1rem;
  font-size: 1.08rem;
  font-weight: 500;
  transition: background 0.2s;
  box-shadow: none;
  font-family: "Inter", sans-serif;
  min-height: calc(1.08rem * 3);
  resize: vertical;

  /* Hide default scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1924;
    border: 1px solid #242424;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333333;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #363636;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
    border: none;
  }

  &::placeholder {
    color: #6b6b6b;
    opacity: 1;
    font-weight: 400;
  }
`;

const ComposerInput = styled.input`
  flex: 1;
  min-width: 0;
  background: #222222;
  color: #bdbdbd;
  border: none;
  outline: none;
  border-radius: 14px;
  padding: 1rem;
  font-size: 1.08rem;
  font-weight: 500;
  transition: background 0.2s;
  box-shadow: none;
  box-sizing: border-box;

  &::placeholder {
    color: #6b6b6b;
    opacity: 1;
    font-weight: 400;
  }
`;

const ComposerActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-left: calc(52px + 1rem);
  flex: 1;
`;

const ComposerAction = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: #222222;
  color: #bdbdbd;
  font-weight: 600;
  border: none;
  border-radius: 16px;
  padding: 0.8rem 0rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
  flex: 1;

  svg {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    #photo-span,
    #video-span {
      display: none;
    }
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-left: calc(62px + 1rem);
`;

const PreviewItem = styled.div`
  position: relative;
  display: inline-block;

  img,
  video {
    max-height: 120px;
    border-radius: 12px;
    object-fit: cover;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 18px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const ModalContent = styled.div`
  background: #282828;
  border-radius: 24px;
  overflow: hidden;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    color: #fff;
    margin: 0;
    font-size: 1.5rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #bdbdbd;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #333;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const CancelButton = styled.button`
  background: #333;
  color: #fff;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #444;
  }
`;

const PostButton = styled.button`
  background: #ff8e53;
  color: #fff;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #ff7a3d;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const StyledEmpty = styled(Empty)`
  .ant-empty-image {
    svg {
      fill: #686868;
    }
  }
`;

export default QA;
